import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { formatCalendarDate, money, relativeTime } from "../../lib/format";
import { useAdminUsers } from "../../lib/queries";
import { EmptyRow, PageError, Pill } from "./shared";

const KYC = [{ value: "", label: "Todo KYC" }, { value: "pending", label: "KYC pendiente" }, { value: "verified", label: "KYC verificado" }, { value: "rejected", label: "KYC rechazado" }];
const ROLES = [{ value: "", label: "Todos los roles" }, { value: "customer", label: "Clientes" }, { value: "admin", label: "Administradores" }, { value: "lender", label: "Aliados" }, { value: "agent", label: "Agentes" }];
const STATUSES = [{ value: "", label: "Cualquier estado" }, { value: "active", label: "Activas" }, { value: "suspended", label: "Suspendidas" }, { value: "closed", label: "Cerradas" }, { value: "pending", label: "Pendientes" }];

/** Every account, searchable and filterable; the URL carries the filters so the overview can deep-link here. */
export function AdminUsersPage() {
  usePageHeader("Usuarios", "Cuentas, wallets, saldos y términos del viaje de cada cliente.");
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") ?? "");
  useEffect(() => { setSearch(params.get("q") ?? ""); }, [params]);
  const kyc = params.get("kyc") ?? "";
  const role = params.get("rol") ?? "";
  const status = params.get("estado") ?? "";
  const users = useAdminUsers(search.trim());
  const set = (key: string, value: string) => { const next = new URLSearchParams(params); if (value) next.set(key, value); else next.delete(key); setParams(next); };
  const filtered = useMemo(() => (users.data ?? []).filter((user) =>
    (!kyc || user.kycStatus === kyc) && (!role || user.role === role) && (!status || user.status === status)
  ), [users.data, kyc, role, status]);
  const totals = useMemo(() => ({
    balance: filtered.reduce((sum, user) => sum + user.totalAvailable, 0),
    wallets: filtered.reduce((sum, user) => sum + user.walletCount, 0)
  }), [filtered]);
  return (
    <div className="page-container">
      <div className="discover-toolbar">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nombre o correo" aria-label="Buscar usuarios" />
        </label>
        <div className="chip-row" role="tablist" aria-label="Filtrar por KYC">
          {KYC.map((item) => <button key={item.value || "all"} type="button" role="tab" aria-selected={kyc === item.value} className={`chip${kyc === item.value ? " active" : ""}`} onClick={() => set("kyc", item.value)}>{item.label}</button>)}
        </div>
        <select className="standalone-input small" value={role} onChange={(e) => set("rol", e.target.value)} aria-label="Filtrar por rol">{ROLES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <select className="standalone-input small" value={status} onChange={(e) => set("estado", e.target.value)} aria-label="Filtrar por estado">{STATUSES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <span className="toolbar-count">{filtered.length} cuentas · {totals.wallets} wallets · <span translate="no">{money.format(totals.balance)}</span></span>
      </div>
      {users.isLoading && <div className="page-state"><Spinner label="Cargando usuarios" /></div>}
      {users.isError && <PageError title="No pudimos cargar los usuarios" retry={() => void users.refetch()} />}
      {users.data && (
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Cuenta</th><th>Rol · Estado</th><th>KYC</th><th>Wallets</th><th>Saldo</th><th>Viaje</th><th>Alta</th><th>Último acceso</th></tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr key={user.id}>
                  <td><Link to={`/admin/usuarios/${user.id}`} className="table-primary"><strong>{user.fullName}</strong><span>{user.email}</span></Link></td>
                  <td><div className="pill-row tight"><Pill status={user.role} tone="info" /><Pill status={user.status} /></div></td>
                  <td><Pill status={user.kycStatus} /></td>
                  <td>{user.walletCount}</td>
                  <td translate="no">{money.format(user.totalAvailable)}</td>
                  <td>{user.trip?.departureDate || user.trip?.destination ? <div className="table-primary"><strong>{user.trip.destination ?? "Viaje"}</strong><span>Sale {formatCalendarDate(user.trip.departureDate)}{user.trip.finalPaymentDate ? ` · paga ${formatCalendarDate(user.trip.finalPaymentDate)}` : ""}</span></div> : <span className="muted">—</span>}</td>
                  <td>{relativeTime(user.createdAt)}</td>
                  <td>{user.lastLoginAt ? relativeTime(user.lastLoginAt) : "—"}</td>
                </tr>
              ))}
              {filtered.length === 0 && <EmptyRow colSpan={8}>Sin resultados.</EmptyRow>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
